import { Inject, Injectable } from '@angular/core';
import { APP_NAME } from '@freelancer/config';
import { Deposits } from '@freelancer/deposits';
import {
  PaymentsErrorType,
  PaymentsMessagingService,
  PaymentsResultStatus,
} from '@freelancer/payments-messaging';
import { Pwa } from '@freelancer/pwa';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DepositMethodApi } from 'api-typings/common/common';
import type { DepositRequestApi } from 'api-typings/payments/payments';
import { firstValueFrom } from 'rxjs';
import type {
  ApplePayDepositRequest,
  ApplePayPaymentRequest,
} from './apple-pay.model';

/**
 * Service to handle Apple Pay payments based on the Apple Pay JS API
 */
@UntilDestroy({ className: 'ApplePay' })
@Injectable({
  providedIn: 'root',
})
export class ApplePay {
  APPLE_PAY_VERSION = 3;

  constructor(
    @Inject(APP_NAME) private appName: string,
    private deposits: Deposits,
    private paymentsMessaging: PaymentsMessagingService,
    private pwa: Pwa,
  ) {}

  /**
   * Check if the current device supports Apple Pay
   */
  canMakePayments(): boolean {
    return (
      !this.pwa.isNative() &&
      window.ApplePaySession &&
      window.ApplePaySession.supportsVersion(this.APPLE_PAY_VERSION) &&
      window.ApplePaySession.canMakePayments()
    );
  }

  /**
   * Make an Apple Pay payment
   */
  async makePayment(request: ApplePayDepositRequest): Promise<void> {
    if (!this.canMakePayments()) {
      throw new Error('Apple Pay is not supported');
    }

    const applePayRequest: ApplePayPaymentRequest = {
      countryCode: request.countryCode,
      currencyCode: request.currency.code,
      supportedNetworks: ['visa', 'masterCard', 'amex', 'discover'],
      merchantCapabilities: ['supports3DS'],
      total: {
        label:
          this.appName === 'freightlancer' ? 'Loadshift' : 'Freelancer.com',
        amount: request.totalAmount.toString(),
      },
    };

    // Starts a new Apple Pay Session
    const session = new window.ApplePaySession(
      this.APPLE_PAY_VERSION,
      applePayRequest,
    );

    let invoiceId: number | undefined;

    /**
     * Requests a merchant session from the backend and completes the merchant validation.
     */
    session.onvalidatemerchant = async (event: any) => {
      const payload: DepositRequestApi = {
        amount: request.depositAmount,
        currency_id: request.currency.id,
        country_code: request.countryCode,
        deposit_method: DepositMethodApi.APPLEPAY,
        user_id: request.userId,
        cart_id: request.cartId,
        confirm_url: event.validationURL,
        threatmetrix_session: request.threatmetrixSession,
        back_url: request.backUrl,
        tracking_token: request.trackingToken,
        cancel_url: request.cancelUrl,
        action: request.action,
        domain: request.domain,
      };
      const response = await firstValueFrom(
        this.deposits.deposit(payload).pipe(untilDestroyed(this)),
      );
      if (response.status === 'success') {
        invoiceId = response.result.invoice?.id;
        const merchantSession = {
          epochTimestamp: response.result.apple_pay_session?.epoch_timestamp,
          expiresAt: response.result.apple_pay_session?.expires_at,
          merchantSessionIdentifier:
            response.result.apple_pay_session?.merchant_session_identifier,
          nonce: response.result.apple_pay_session?.nonce,
          merchantIdentifier:
            response.result.apple_pay_session?.merchant_identifier,
          domainName: response.result.apple_pay_session?.domain_name,
          displayName: response.result.apple_pay_session?.display_name,
          signature: response.result.apple_pay_session?.signature,
        };
        session.completeMerchantValidation(merchantSession);
      } else {
        session.abort();
        this.paymentsMessaging.pushError({
          errorCode: response.errorCode,
          errorType: PaymentsErrorType.ERROR,
          eventId: response.requestId,
        });
      }
    };

    /**
     * Payment has been authorized. Complete the payment by sending the token.
     */
    session.onpaymentauthorized = async (event: any) => {
      const payload = {
        user_id: request.userId,
        deposit_method: DepositMethodApi.APPLEPAY,
        paypal_token: btoa(JSON.stringify(event.payment.token.paymentData)),
        invoice_id: invoiceId,
      };

      const response = await this.deposits.depositUpdate(payload);
      if (response.status === 'success') {
        session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
        this.paymentsMessaging.pushMessage({
          paymentsStatus: PaymentsResultStatus.SUCCESS,
          method: DepositMethodApi.APPLEPAY,
        });
      } else {
        session.completePayment(window.ApplePaySession.STATUS_FAILURE);
        this.paymentsMessaging.pushError({
          errorCode: response.errorCode,
          errorType: PaymentsErrorType.ERROR,
          eventId: response.requestId,
        });
      }
    };

    /**
     * User has cancelled the payment in the Apple Pay payment sheet.
     */
    session.oncancel = () => {
      this.paymentsMessaging.pushMessage({
        paymentsStatus: PaymentsResultStatus.INTERRUPTED,
        method: DepositMethodApi.APPLEPAY,
      });
    };

    /**
     * Begin the Apple Pay transaction.
     */
    session.begin();
  }
}
